class MCartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", async (t) => {
      t.preventDefault();
      t.stopPropagation();

      // Show loading state
      const originalText = this.innerHTML;
      this.innerHTML = "<span>Removing...</span>";
      this.style.opacity = "0.5";

      try {
        const cartComponent =
          this.closest("m-cart") || this.closest("m-cart-drawer-items");
        if (cartComponent) {
          const index = this.dataset.index;
          if (index) {
            // Direct AJAX call to remove the item
            const response = await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                line: index,
                quantity: 0,
              }),
            });

            if (!response.ok) {
              throw new Error("Failed to remove item");
            }

            const cartData = await response.json();

            // Update cart UI without page reload
            if (
              cartComponent.refreshCart &&
              typeof cartComponent.refreshCart === "function"
            ) {
              // Use the new refreshCart method
              cartComponent.refreshCart();
            } else {
              // Fallback to old methods
              if (cartComponent.mainCartItems) {
                const itemToRemove = this.closest(".m-cart-item");
                if (itemToRemove) {
                  itemToRemove.style.opacity = "0";
                  itemToRemove.style.height = "0";
                  itemToRemove.style.overflow = "hidden";
                  itemToRemove.style.transition = "all 0.3s ease";

                  // Remove the element after animation
                  setTimeout(() => {
                    itemToRemove.remove();
                  }, 300);
                }
              }

              // Update cart count
              if (cartComponent.updateCartCount) {
                cartComponent.updateCartCount(cartData.item_count);
              }

              // Update cart prices
              if (cartComponent.updateCartPrices) {
                cartComponent.updateCartPrices(cartData);
              }

              // Update cart subtotal
              const subtotalElement = document.querySelector(
                "[data-cart-subtotal]"
              );
              if (subtotalElement && cartData.total_price !== undefined) {
                if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
                  subtotalElement.innerHTML = Shopify.formatMoney(
                    cartData.total_price
                  );
                } else {
                  subtotalElement.innerHTML = `£${(
                    cartData.total_price / 100
                  ).toFixed(2)}`;
                }
              }

              // Check if cart is empty
              if (cartData.item_count === 0) {
                // Show empty cart message
                const emptyCartMessage = document.createElement("div");
                emptyCartMessage.className = "m-cart-empty-message";
                emptyCartMessage.innerHTML = "<p>Your cart is empty</p>";

                const cartItemsContainer = document.querySelector(
                  "[data-minimog-cart-items]"
                );
                if (cartItemsContainer) {
                  cartItemsContainer.innerHTML = "";
                  cartItemsContainer.appendChild(emptyCartMessage);
                }

                // Update cart drawer state if it exists
                if (cartComponent.cartDrawerWrapper) {
                  cartComponent.cartDrawerWrapper.classList.add("is-empty");
                }
              }
            }

            // Show success message
            const successMessage = document.createElement("div");
            successMessage.className = "success-message";
            successMessage.textContent = "Item removed from cart!";
            document.body.appendChild(successMessage);

            setTimeout(() => {
              successMessage.remove();
            }, 3000);

            // Emit cart update event
            if (window.MinimogEvents && window.MinimogTheme) {
              MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
                ...cartData,
                source: "main-cart-items",
              });
            }
          }
        }
      } catch (error) {
        console.error("Error removing item:", error);
        // Restore original button state
        this.innerHTML = originalText;
        this.style.opacity = "1";

        // Show error message
        const errorMessage = document.createElement("div");
        errorMessage.className = "error-message";
        errorMessage.textContent = "Could not remove item. Please try again.";
        document.body.appendChild(errorMessage);

        setTimeout(() => {
          errorMessage.remove();
        }, 3000);
      }
    });
  }
}
customElements.define("m-cart-remove-button", MCartRemoveButton);
class MCartTemplate extends HTMLElement {
  constructor() {
    super(),
      (this.cartUpdateUnsubscriber = void 0),
      (this.isCartPage = "cart" === MinimogSettings.templateName),
      (this.cartDrawerWrapper = document.querySelector("m-cart-drawer")),
      (this.cartDrawerInner = document.querySelector(".m-cart-drawer__inner")),
      (this.mainCartItems = this.querySelector("[data-minimog-cart-items]")),
      (this.cartSubTotal = this.querySelector("[data-cart-subtotal]")),
      (this.cartDiscount = this.querySelector("[data-minimog-cart-discounts]")),
      (this.giftWrapping = this.querySelector("[data-minimog-gift-wrapping]"));
    let t = this.cartDrawerInner;
    this.isCartPage && (t = document.body),
      (this.loading = new MinimogLibs.AnimateLoading(t, { overlay: t })),
      (this.rootUrl = window.Shopify.routes.root);
    const e = debounce((t) => {
      "id" !== t.target.name && this.onChange(t);
    }, 300);
    this.isCartPage
      ? this.mainCartItems.addEventListener("change", e.bind(this))
      : this.addEventListener("change", e.bind(this)),
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (t) => {
        this.getCart().then((t) => {
          this.updateCartCount(t.item_count);
          this.updateCartPrices(t);
        });
      });
  }
  updateCartPrices(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return;

    let updatedTotal = 0;

    // Loop through each item and check for custom pricing
    cart.items.forEach((item) => {
      if (item.properties && item.properties._price) {
        const customPrice = parseInt(item.properties._price);
        const quantity = item.quantity;
        // Use the custom price directly instead of multiplying by quantity
        // This ensures we use the bundle price, not individual item price × quantity
        const totalItemPrice = customPrice;

        // Find the price element for this item and update it
        const itemPriceElements = document.querySelectorAll(
          `[data-cart-item-price="${item.key}"]`
        );
        if (itemPriceElements.length > 0) {
          itemPriceElements.forEach((element) => {
            // Format the price as currency
            const formattedPrice = this.formatMoney(totalItemPrice);
            element.innerHTML = formattedPrice;
          });
        }

        updatedTotal += totalItemPrice;
      } else {
        // Use the standard price
        updatedTotal += item.final_line_price;
      }
    });

    // Update the cart subtotal
    const subtotalElements = document.querySelectorAll("[data-cart-subtotal]");
    if (subtotalElements.length > 0) {
      subtotalElements.forEach((element) => {
        const formattedTotal = this.formatMoney(updatedTotal);
        element.innerHTML = formattedTotal;
      });
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }

    // Fallback formatter if Shopify.formatMoney is not available
    const value = (cents / 100).toFixed(2);
    return `£${value}`;
  }
  updateCartCount(t) {
    document.querySelectorAll(".m-cart-count-bubble").forEach((e) => {
      t > 0
        ? ((e.textContent = t), e.classList.remove("m:hidden"))
        : e.classList.add("m:hidden");
    });
  }
  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }
  connectedCallback() {
    this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
      MinimogTheme.pubSubEvents.cartUpdate,
      (t) => {
        "main-cart-items" !== t.source && this.onCartUpdate();
      }
    );

    // Initialize cart prices when the component is connected
    this.getCart().then((cartData) => {
      this.updateCartPrices(cartData);
    });
  }
  disconnectedCallback() {
    this.cartUpdateUnsubscriber && this.cartUpdateUnsubscriber();
  }
  onCartUpdate(t = !0) {
    const { routes: e } = window.MinimogSettings;
    fetch(`${e.cart}?section_id=cart-template`)
      .then((t) => t.text())
      .then((e) => {
        const i = new DOMParser().parseFromString(e, "text/html"),
          n = i.querySelector("[data-minimog-cart-items]"),
          r = i.querySelector("[data-cart-subtotal]"),
          a = i.querySelector("[data-minimog-cart-discounts]"),
          s = i.querySelector("[data-minimog-gift-wrapping]");
        this.isCartPage &&
          ((this.mainCartItems.innerHTML = n.innerHTML),
          t &&
            ((this.cartSubTotal.innerHTML = r.innerHTML),
            (this.cartDiscount.innerHTML = a.innerHTML),
            (this.giftWrapping.innerHTML = s.innerHTML)),
          window.FoxKitV2 &&
            window.FoxKitV2.Modules.InCart &&
            window.FoxKitV2.Modules.InCart.getCart());

        // Get the cart data and update prices
        this.getCart().then((cartData) => {
          this.updateCartPrices(cartData);
        });
      })
      .catch((t) => {});
  }
  onChange(t) {
    this.updateQuantity(
      t.target.dataset.index,
      t.target.value,
      document.activeElement.getAttribute("name")
    );
  }
  async updateQuantity(t, e, i) {
    this.loading.start();
    const { routes: n } = window.MinimogSettings;

    try {
      // If removing item (quantity = 0), use standard approach immediately
      if (e == 0) {
        await this.standardUpdateQuantity(t, e, n);
        return;
      }

      // Get current cart to check for custom pricing
      const currentCart = await fetchJSON(this.rootUrl + "cart.json");
      const lineItem = currentCart.items[t - 1];

      // Check if this item has custom pricing
      if (lineItem && lineItem.properties && lineItem.properties._price) {
        const customPrice = lineItem.properties._price;
        const bundleName = lineItem.properties._bundle;

        // Determine the correct price based on the bundle name
        let updatedPrice = customPrice;
        if (bundleName === "1 Jar") {
          updatedPrice = 2500;
        } else if (bundleName === "2 Jars") {
          updatedPrice = 3999;
        } else if (bundleName === "3 Jars") {
          updatedPrice = 6299;
        }

        // For custom priced items, we need a special approach
        // Instead of clearing the cart, update just this line item
        const r = JSON.stringify({
          line: t,
          quantity: e,
          properties: {
            _bundle: bundleName,
            _price: updatedPrice,
          },
          sections: this.getSectionsToRender().map((t) => t.section),
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${n.cart_change_url}`, {
          ...fetchConfig(),
          body: r,
        });
        const responseData = await response.text();
        const cartData = JSON.parse(responseData);

        // If there was an error with the update, try the fallback approach
        if (cartData.errors) {
          console.log("Using fallback approach for custom pricing");

          // Save all other items in the cart
          const otherItems = currentCart.items.filter(
            (item, index) => index !== t - 1
          );
          const savedItems = [];

          for (const item of otherItems) {
            const itemData = {
              id: item.variant_id,
              quantity: item.quantity,
            };

            if (item.properties) {
              itemData.properties = item.properties;
            }

            savedItems.push(itemData);
          }

          // Clear the cart
          await fetch("/cart/clear.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          // Add the updated item with custom price
          const formData = new FormData();
          formData.append("id", lineItem.variant_id);
          formData.append("quantity", e);
          formData.append("properties[_bundle]", bundleName);
          formData.append("properties[_price]", updatedPrice);

          await fetch("/cart/add.js", {
            method: "POST",
            body: formData,
          });

          // Add back all other items
          if (savedItems.length > 0) {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                items: savedItems,
              }),
            });
          }

          // Update cart attributes
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              attributes: {
                _bundle_price: updatedPrice,
              },
              note: `Bundle: ${bundleName} - Price: £${
                parseInt(updatedPrice) / 100
              }`,
            }),
          });
        }

        // Get updated cart sections
        const sectionsResponse = await fetch(
          `${n.cart}?section_id=cart-template`
        );
        const sectionsHtml = await sectionsResponse.text();

        // Update the cart UI
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionsHtml, "text/html");

        this.getSectionsToRender().forEach((section) => {
          const element =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          const sectionElement = doc.querySelector(section.selector);

          if (element && sectionElement) {
            element.innerHTML = sectionElement.innerHTML;
          }
        });

        // Get the updated cart
        const updatedCart = await fetchJSON(this.rootUrl + "cart.json");

        // Emit cart update event
        MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
          ...updatedCart,
          source: "main-cart-items",
        });
      } else {
        // For standard items, use the normal update process
        await this.standardUpdateQuantity(t, e, n);
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      this.loading.finish();
    }
  }

  // Standard update quantity method for regular items
  async standardUpdateQuantity(t, e, n) {
    // If removing item (quantity = 0), log for debugging
    if (e == 0) {
      console.log(`Removing item at line ${t}`);
    }

    const r = JSON.stringify({
      line: t,
      quantity: e,
      sections: this.getSectionsToRender().map((t) => t.section),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${n.cart_change_url}`, {
        ...fetchConfig(),
        body: r,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      const data = JSON.parse(responseText);
      const { items } = data;

      this.updateLiveRegions(data.item_count, items);

      // Update cart items section
      const cartItemsElement = document.getElementById("main-cart-items");
      if (cartItemsElement) {
        const cartItemsHTML = await this.getSectionInnerHTML(
          responseText,
          cartItemsElement.dataset.id,
          ".cart-items"
        );
        cartItemsElement.innerHTML = cartItemsHTML;

        const cartItemsComponent = cartItemsElement.querySelector("cart-items");
        if (
          cartItemsComponent &&
          typeof cartItemsComponent.updateQuantity === "function"
        ) {
          cartItemsComponent.updateQuantity();
        }
      }

      // Update cart footer section
      const cartFooterElement = document.getElementById("main-cart-footer");
      if (cartFooterElement) {
        const cartFooterHTML = await this.getSectionInnerHTML(
          responseText,
          cartFooterElement.dataset.id,
          ".cart-footer"
        );
        cartFooterElement.innerHTML = cartFooterHTML;

        // Check if cart is empty and update UI accordingly
        const isCartEmpty = data.item_count === 0;
        if (this.cartDrawerWrapper) {
          const wasEmpty =
            this.cartDrawerWrapper.classList.contains("is-empty");
          if ((wasEmpty && !isCartEmpty) || (!wasEmpty && isCartEmpty)) {
            this.onCartUpdate(false);
          }
        }
      }

      // Update cart icon bubble
      const cartIconElement = document.getElementById("cart-icon-bubble");
      if (cartIconElement) {
        const cartIconHTML = await this.getSectionInnerHTML(
          responseText,
          cartIconElement.dataset.id,
          ".m-cart-count-bubble"
        );
        cartIconElement.innerHTML = cartIconHTML;

        // Check if cart is empty and update UI accordingly
        const isCartEmpty = data.item_count === 0;
        if (this.cartDrawerWrapper) {
          const wasEmpty =
            this.cartDrawerWrapper.classList.contains("is-empty");
          if ((wasEmpty && !isCartEmpty) || (!wasEmpty && isCartEmpty)) {
            this.onCartUpdate(false);
          }
        }
      }

      // Get the cart data and update prices
      const cartData = await this.getCart();
      this.updateCartPrices(cartData);

      // Emit cart update event
      MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
        ...data,
        source: "main-cart-items",
      });
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      this.loading.finish();
    }
  }

  updateLiveRegions(t, e) {
    if (
      (this.cartItemKey &&
        document
          .getElementById(`Line-item-error-${this.cartItemKey}`)
          ?.querySelector(".cart-item__error-text").innerHTML ===
          window.cartStrings.error) ||
      (document
        .getElementById("cart-errors")
        ?.innerHTML.includes(window.cartStrings.error) &&
        0 === e.length)
    )
      return;
    this.cartItemKey = null;
    const n = document.getElementById("cart-live-region-text"),
      r = n?.getElementsByTagName("div");
    r?.length && (r[0].innerHTML = window.cartStrings.quantityError);
  }
  getSectionInnerHTML(t, e, n) {
    return this.getSectionDOM(t, e, n).then((t) => t.innerHTML);
  }
  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: "main-cart-items",
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".m-cart-count-bubble",
      },
      {
        id: "main-cart-footer",
        section: "main-cart-footer",
        selector: ".js-contents",
      },
    ];
  }
  refreshCart() {
    // Show loading indicator if available
    if (this.loading && typeof this.loading.start === "function") {
      this.loading.start();
    }

    // Get the latest cart data
    this.getCart()
      .then((cartData) => {
        // Update cart count
        this.updateCartCount(cartData.item_count);

        // Update cart prices
        this.updateCartPrices(cartData);

        // Fetch the cart template HTML
        return fetch(
          `${window.MinimogSettings.routes.cart}?section_id=cart-template`
        );
      })
      .then((response) => response.text())
      .then((html) => {
        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Get the cart items HTML
        const newCartItems = doc.querySelector("[data-minimog-cart-items]");

        // Update the cart items in the DOM
        if (this.mainCartItems && newCartItems) {
          this.mainCartItems.innerHTML = newCartItems.innerHTML;
        }

        // Update other cart elements
        if (this.cartSubTotal) {
          const newSubtotal = doc.querySelector("[data-cart-subtotal]");
          if (newSubtotal) {
            this.cartSubTotal.innerHTML = newSubtotal.innerHTML;
          }
        }

        if (this.cartDiscount) {
          const newDiscount = doc.querySelector(
            "[data-minimog-cart-discounts]"
          );
          if (newDiscount) {
            this.cartDiscount.innerHTML = newDiscount.innerHTML;
          }
        }

        if (this.giftWrapping) {
          const newGiftWrapping = doc.querySelector(
            "[data-minimog-gift-wrapping]"
          );
          if (newGiftWrapping) {
            this.giftWrapping.innerHTML = newGiftWrapping.innerHTML;
          }
        }

        // Hide loading indicator if available
        if (this.loading && typeof this.loading.finish === "function") {
          this.loading.finish();
        }

        // Emit cart update event
        if (window.MinimogEvents && window.MinimogTheme) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
            source: "cart-template",
          });
        }
      })
      .catch((error) => {
        console.error("Error refreshing cart:", error);

        // Hide loading indicator if available
        if (this.loading && typeof this.loading.finish === "function") {
          this.loading.finish();
        }
      });
  }
}
customElements.define("m-cart", MCartTemplate);

class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.cartTemplate = document.querySelector("m-cart");

    // Add open method if it doesn't exist
    if (!this.open) {
      this.open = this.openDrawer.bind(this);
    }

    // Add close method if it doesn't exist
    if (!this.close) {
      this.close = this.closeDrawer.bind(this);
    }

    // Add toggle method if it doesn't exist
    if (!this.toggle) {
      this.toggle = this.toggleDrawer.bind(this);
    }

    // Make the drawer globally accessible
    window.cartDrawer = this;

    // Expose the open method globally
    window.openCartDrawer = this.open;

    // Add event listeners for close buttons
    const closeButtons = this.querySelectorAll(".m-cart-drawer__close");
    closeButtons.forEach((button) => {
      button.addEventListener("click", this.close);
    });

    // Add event listener for overlay
    const overlay = this.querySelector(".m-cart-drawer__overlay");
    if (overlay) {
      overlay.addEventListener("click", this.close);
    }

    // Add event listener for ESC key
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
  }

  openDrawer() {
    this.isOpen = true;
    this.classList.add("is-open");
    document.body.classList.add("overflow-hidden");

    // Refresh cart content
    if (
      this.cartTemplate &&
      typeof this.cartTemplate.refreshCart === "function"
    ) {
      this.cartTemplate.refreshCart();
    }

    // Emit drawer open event
    if (window.MinimogEvents && window.MinimogTheme) {
      MinimogEvents.emit(MinimogTheme.pubSubEvents.drawerOpen, {
        drawer: "cart",
      });
    }
  }

  closeDrawer() {
    this.isOpen = false;
    this.classList.remove("is-open");
    document.body.classList.remove("overflow-hidden");

    // Emit drawer close event
    if (window.MinimogEvents && window.MinimogTheme) {
      MinimogEvents.emit(MinimogTheme.pubSubEvents.drawerClose, {
        drawer: "cart",
      });
    }
  }

  toggleDrawer() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

// Define the custom element
customElements.define("m-cart-drawer", MCartDrawer);
